import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useStores } from "stores";

import type { ReleaseUrlParams } from "components/ReleaseForm/types";
import { revisedFormService } from "services";
import { PrimaryFormNames, ReleaseCommentCount, ReleaseStatus, SecondaryFormNames, TertiaryFormNames } from "types";

/**
 * Hook for fetching and managing comment counts for secondary and tertiary forms.
 *
 * This hook handles:
 * - Fetching comment counts from the API for secondary and tertiary forms.
 * - Calculating total comments.
 * - Managing comment state in the `revisedFormStore`.
 *
 * @returns Object containing functions to fetch, update, and reset comment states.
 */
export const useFetchComments = () => {
  const { userId = null, releaseId } = useParams<ReleaseUrlParams>(); // Extract `userId` and `releaseId` from URL
  const {
    revisedFormStore: {
      release,
      releaseComments: { setCommentState, resetCommentsTotal },
    },
  } = useStores();

  /**
   * Safely fetches comment counts for a secondary form.
   *
   * @param {SecondaryFormNames} secondaryFormName - Name of the secondary form to fetch comments for.
   * @returns {Promise<ReleaseCommentCount>} Object containing `numCommentsForUser` and `numCommentsForAdmin`.
   */
  const safeFetchSecondaryComments = async (secondaryFormName: SecondaryFormNames): Promise<ReleaseCommentCount> => {
    try {
      return await revisedFormService.getSecondaryCommentList({
        userId,
        primaryFormName: PrimaryFormNames.Releases,
        primaryFormId: releaseId!,
        secondaryFormName,
      });
    } catch (error) {
      return { numCommentsForUser: 0, numCommentsForAdmin: 0 }; // Default fallback in case of an error
    }
  };

  /**
   * Safely fetches comment counts for a tertiary form.
   *
   * @param {SecondaryFormNames} secondaryFormName - Name of the related secondary form.
   * @param {TertiaryFormNames} tertiaryFormName - Name of the tertiary form to fetch comments for.
   * @returns {Promise<ReleaseCommentCount>} Object containing `numCommentsForUser` and `numCommentsForAdmin`.
   */
  const safeFetchTertiaryComments = async (
    secondaryFormName: SecondaryFormNames,
    tertiaryFormName: TertiaryFormNames,
  ): Promise<ReleaseCommentCount> => {
    try {
      return await revisedFormService.getTertiaryCommentList({
        userId,
        primaryFormName: PrimaryFormNames.Releases,
        primaryFormId: releaseId!,
        secondaryFormName,
        tertiaryFormName,
      });
    } catch (error) {
      return { numCommentsForUser: 0, numCommentsForAdmin: 0 }; // Default fallback in case of an error
    }
  };

  /**
   * Calculates the total comment count from an array of comment objects.
   *
   * @param {ReleaseCommentCount[]} comments - Array of comment count objects.
   * @returns {ReleaseCommentCount} Object with total `numCommentsForUser` and `numCommentsForAdmin`.
   */
  const calculateTotalComments = (comments: ReleaseCommentCount[]): ReleaseCommentCount => {
    return comments.reduce(
      (acc, comment) => {
        acc.numCommentsForUser += comment.numCommentsForUser;
        acc.numCommentsForAdmin += comment.numCommentsForAdmin;
        return acc;
      },
      { numCommentsForUser: 0, numCommentsForAdmin: 0 }, // Initial accumulator values
    );
  };

  /**
   * Fetches and updates the comment state for works.
   */
  const fetchWorksComments = useCallback(async () => {
    if (!releaseId) return;
    const works = await safeFetchSecondaryComments(SecondaryFormNames.ReleaseWorks);
    const worksRevisedComments = await Promise.all([
      safeFetchTertiaryComments(SecondaryFormNames.ReleaseWorks, TertiaryFormNames.ReleaseSongwriters),
      safeFetchTertiaryComments(SecondaryFormNames.ReleaseWorks, TertiaryFormNames.ReleasePublishers),
    ]);
    const allWorksComments = [works, ...worksRevisedComments.flat()];
    const totalWorksComments: ReleaseCommentCount = calculateTotalComments(allWorksComments);
    setCommentState(SecondaryFormNames.ReleaseWorks, totalWorksComments);
  }, [releaseId]);

  /**
   * Fetches and updates the comment state for tracks.
   */
  const fetchTracksComments = useCallback(async () => {
    if (!releaseId) return;
    const tracks = await safeFetchSecondaryComments(SecondaryFormNames.ReleaseTracks);
    const tracksRevisedComments = await Promise.all([
      safeFetchTertiaryComments(SecondaryFormNames.ReleaseTracks, TertiaryFormNames.ReleaseTrackArtists),
      safeFetchTertiaryComments(SecondaryFormNames.ReleaseTracks, TertiaryFormNames.ReleaseContributors),
    ]);
    const allTracksComments = [tracks, ...tracksRevisedComments.flat()];
    const totalTracksComments: ReleaseCommentCount = calculateTotalComments(allTracksComments);
    setCommentState(SecondaryFormNames.ReleaseTracks, totalTracksComments);
  }, [releaseId]);

  /**
   * Fetches and updates the comment states for all relevant forms based on release status.
   *
   * @param {ReleaseStatus | null} status - The current release status.
   */
  const retrieveCommentState = async (status?: ReleaseStatus | null) => {
    if (status === (ReleaseStatus.Draft || ReleaseStatus.Released) || !release?.status) return;
    try {
      await Promise.all([fetchWorksComments(), fetchTracksComments()]);
    } finally {
      null;
    }
  };

  /**
   * Updates the comment state for a specific form type.
   *
   * @param {SecondaryFormNames} commentsType - The type of the secondary form to update.
   */
  const updateCommentState = async (commentsType: SecondaryFormNames) => {
    switch (commentsType) {
      case SecondaryFormNames.ReleaseWorks:
        return fetchWorksComments();
      case SecondaryFormNames.ReleaseTracks:
        return fetchTracksComments();
      case SecondaryFormNames.ReleaseArtists:
        return; // No additional logic for `ReleaseArtists`
    }
  };

  return { fetchWorksComments, fetchTracksComments, retrieveCommentState, resetCommentsTotal, updateCommentState };
};
